import { PaginatedListOptionProps, PaginatedListProps } from 'components/forms/inputs/rhf-inputs/usePaginatedList';
import type { DataTableColumnDef } from 'components/tables/data-table';
import type { ComponentProps } from 'react';
import type { FormattedMessage } from 'react-intl';
import { QueryResponse } from 'types/paginationAndFilter';

/**
 * What the chrome knows about an empty result and a `renderCustomTable` implementation does not:
 * whether the list is tenant-empty, narrowed by a filter, or failed outright, plus the callbacks
 * that undo the first two. Spread straight into an `<EmptyState>`.
 */
export type CustomTableEmptyStateProps = {
    variant: 'empty' | 'filtered' | 'error';
    onClearFilters: () => void;
    onRetry: () => void;
};

/**
 * The table-shaped half of {@link ListTableWithFilters}' props: everything that describes the
 * rows themselves, as opposed to the filter/search/export chrome around them. Forwarded to the
 * `DataTable` engine; the chrome owns data, sort state, pagination and the managed column layout,
 * so none of those appear here.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TotalsType defaults to `any` for back-compat with concrete-typed renderTotal callbacks; tightening to `unknown` breaks contravariance, since function parameters are contravariant. Consumers may specify TotalsType explicitly.
export type ListTableBaseProps<D extends object, TotalsType = any> = {
    columns?: DataTableColumnDef<D, TotalsType>[];
    /** Content rendered in the expanded sub-row beneath a row (colSpan = visible columns). */
    renderExpandedContent?: (row: D) => JSX.Element;
    /** Full-width block rendered above a row — a grouped list's group ribbon. Return null for rows
     *  that start no group. Must be referentially stable (memoized-body prop). */
    renderRowLeadIn?: (row: D) => JSX.Element | null;
    /** One full-width cell in place of a row's cells; null keeps the row's cells. Must be referentially
     *  stable (memoized-body prop). */
    renderFullRow?: (row: D) => JSX.Element | null;
    /** Full-width block frozen under the header for the row at the top of the viewport — the same
     *  group ribbon, kept on screen while its group is. Asked about EVERY row, not just the one that
     *  opens a group, so it resolves to that row's group. Return null for no block. */
    renderStickyLeadIn?: (row: D) => JSX.Element | null;
    /** Replace the table entirely (card/grouped views that have no column grid). Wins over the
     *  DataTable branch, so such a table never touches the column engine. */
    renderCustomTable?: (
        dataToDisplay: D[],
        initialLoad: boolean,
        noData: boolean,
        textId?: string,
        paginationProps?: Partial<QueryResponse<D>>,
        handleLoadMore?: () => void,
        emptyStateProps?: CustomTableEmptyStateProps
    ) => JSX.Element;
    isLoading: boolean;
    /** Background refetch in progress while cached rows are shown. Drives the bottom skeleton
     *  without the blocking overlay, so stale data stays visible during revalidation. */
    isFetching?: boolean;
    onSortChange?: (sortBy: string) => void;
    /**
     * Callback for row clicks. If you have clickable elements (links, buttons) inside the row,
     * make sure to call event.stopPropagation() on them to prevent triggering this handler.
     */
    onRowClick?: (row: D) => void;
    /** The app path of the record a row is about: in the new design a click anywhere on the row opens it,
     *  except on a control. The same path the row's own link opens; undefined for a row with no record.
     *  Must be referentially stable (memoized-body prop). */
    getRowHref?: (row: D) => string | undefined;
    isRowEnabled?: (row: D) => boolean;
    /** Optional accent colour rendered as a thin inset bar on the left and right edges of the
     *  row — used in My Portfolio to highlight rows the user is RP/RM of. Returning a falsy
     *  value (undefined / null) skips the bars for that row. */
    rowEdgeColor?: (row: D) => string | null | undefined;
    /** Optional accent colour applied to the wrapping cell of the expanded sub-row (the cell
     *  that hosts `renderExpandedContent`). Lets callers paint a different colour — or
     *  suppress it — when the parent row is bordered but the expanded block should differ.
     *  Falls back to `rowEdgeColor` when not provided. */
    expandedRowEdgeColor?: (row: D) => string | null | undefined;
    /**
     * Opt-in scroll restoration. When set, the table's internal scroll container remembers and
     * restores its scroll position per URL as the user navigates away (e.g. into a detail page)
     * and back. Pass a stable, unique id per list page (e.g. 'matters-list'). Omit to disable.
     */
    scrollRestorationId?: string;
    /**
     * localStorage persistence key for the user's column widths, order and visibility. Every table
     * resolves per-column pixel widths at runtime from the container width and each column's
     * `widthWeight`/`minPx` meta (with `sticky` pinning); this id is what keeps a resize, reorder or
     * column-picker choice across remounts, and what enables the resize/reorder/picker affordances.
     * Pass a stable, unique id per column set (e.g. 'invoices-<tab>').
     */
    columnConfigId?: string;
};

/**
 * Heterogeneous per-consumer Option shape for `dynamicmultiselect` / `dynamicdropdown` filters.
 * Each consumer wires `paginated.fetch` to a concrete RTK Query endpoint (e.g. contacts,
 * matters, employees), and the adornment callbacks read that endpoint's DTO. Threading an
 * `Option` generic through `DynamicMultiselectFilter` / `DynamicDropdownFilter` cascades
 * into the `MainFilter` discriminated union, which is referenced by 60+ filter-config and
 * page files. `PaginatedListProps<T>.fetch` puts `T` in invariant position via
 * `UseLazyQuery<QueryDefinition<…, QueryResponse<T>, …>>`, so the cascade hits a
 * RHF-style invariance ceiling — consumers must specify the type arg explicitly, no
 * inference. The adornment callbacks additionally rely on `any`'s contravariance to type
 * concrete DTOs at the callback site (`(option: ContactMinDto) => ReactNode`); tightening
 * to `unknown` breaks every existing adornment.
 *
 * Same legitimate-disable category as a library's own generics: a heterogeneous
 * lookup map has no single element type to name.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see DynamicFilterOption JSDoc above; heterogeneous per-consumer Option shape, invariance ceiling via PaginatedListProps.fetch
type DynamicFilterOption = any;

export const NULL_FILTER_SENTINEL = '__NULL_FILTER__';
/** The "is not empty" option a multiselect with a `notEmptyOption` pins under its "is empty" one. */
export const NOT_NULL_FILTER_SENTINEL = '__NOT_NULL_FILTER__';

/** One of the pinned is empty / is not empty options rather than a value. */
export const isEmptinessOption = (option: SelectOption) =>
    option.value === NULL_FILTER_SENTINEL || option.value === NOT_NULL_FILTER_SENTINEL;

interface NullFilterOption {
    label: string;
    tooltip?: string;
}

export interface RealAndVirtualFilterObject {
    virtualFilter: string | undefined;
    realFilter: string | undefined;
}

interface FilterCheckbox {
    label: string;
    argument: string;
    tooltip?: string;
    /** Values interpolated into `tooltip`, for a tooltip whose wording depends on runtime data. */
    tooltipValues?: ComponentProps<typeof FormattedMessage>['values'];
    defaultSelected?: boolean;
    rsqlOverride?: string;
}

export interface SelectOption {
    label: string;
    value: string | number | boolean;
}

export type FilterCheckboxGroup = {
    type?: 'checkbox';
    label?: string;
    /** The name of the group's chip in the new design, an i18n key, for a group of several options with no
     *  `label`: classic draws those options as bare checkboxes, the chip needs a name to show until one is picked. */
    chipLabel?: string;
    selector: string;
    comperator?: string;
    multiple?: boolean;
    /** The new design's chip names a picked option with the group's label, `Is active: yes`, for options such as
     *  Yes and No that say nothing on their own. */
    answerWithLabel?: boolean;
    /** In the new design's yes / no reading of a `true` / `false` pair, the `false` option is Yes. */
    falseIsYes?: boolean;
    filters: FilterCheckbox[];
    /** Locks this group alone against the applied drawer filters. `QuickFilters`' own `disabled`
     *  prop is component-wide, so it cannot express "this one filter no longer applies". */
    getShouldDisable?: (props: { mainFilters: MainFilterForm }) => boolean;
    onChange?: (props: {
        isSelected: boolean;
        value: string;
        groupSelector: string;
        mainFilterState: MainFilterForm;
        handleMainFilterStateChange: (mainFilterState: MainFilterForm) => void;
    }) => void;
};

type QuickFilterDropdownMultiselect = {
    type: 'dropdown-multiselect';
    label: string;
    selector: string;
    comperator?: string;
    options: SelectOption[];
};

type QuickFilterItem = FilterCheckboxGroup | QuickFilterDropdownMultiselect;

export interface FilterCheckboxInForm extends FilterCheckbox {
    isSelected: boolean;
    key: string;
    value: string;
    /** The group's `no` answer, carried as an option so No is exclusive with Yes; never drawn as a checkbox. */
    isNo?: boolean;
}

export type QuickFilterCheckboxGroupForm = Omit<FilterCheckboxGroup, 'filters'> & {
    filters: FilterCheckboxInForm[];
};

export type QuickFilterDropdownMultiselectForm = QuickFilterDropdownMultiselect & {
    selected: SelectOption[];
};

export type QuickFilterFormItem = QuickFilterCheckboxGroupForm | QuickFilterDropdownMultiselectForm;

export type QuickFilterForm = {
    [key: string]: QuickFilterFormItem;
};

/** A named date range a period quick filter offers, such as the current month, worked out from today. */
export type PeriodPreset = {
    value: string;
    label: string;
    range: (today: Date) => { from: Date; to: Date };
};

/**
 * A quick filter onto one of the list's `daterange` main filters, picked as the dashboard picks its period: one
 * preset, or a custom range in the date range dialog. It writes into the main filter, so the drawer shows the same
 * range.
 */
export type PeriodQuickFilter = {
    selector: string;
    presets: PeriodPreset[];
    /** The preset's `value` the period starts from, also when remembered or cleared filters hold no period. */
    defaultPreset?: string;
};

type SearchBarSelector = {
    selector: string;
    comparator?: string;
    arguments?: string[] | (() => string[]);
};

export type FilterConfig = {
    quickFilters: QuickFilterItem[];
    filters: MainFilter[];
    searchBarSelectors: SearchBarSelector[];
    searchBarSelectorsLimitToFirstArg?: string[];
    passiveFilter?: string;
    /** Width of the filter drawer panel in px, when a screen needs other than the drawer's own 744px. */
    mainFilterWidth?: number;
    /** Suppress the vertical divider drawn between checkbox-group filters in the drawer. */
    hideFilterDividers?: boolean;
    /** Draws the quick filters as the new design's chips in classic too; the drawer stays classic. */
    quickFilterChips?: boolean;
    /** A period chip before the quick filter chips; drawn with the chips only. */
    periodQuickFilter?: PeriodQuickFilter;
    /** A tooltip after a filter group's header, by the group's message key: its message key. */
    groupTooltips?: Record<string, string>;
};

type FilterType =
    | 'date'
    | 'offset-date-time'
    | 'daterange'
    | 'offset-date-time-range'
    | 'interval'
    | 'number'
    | 'multiselect'
    | 'dynamicmultiselect'
    | 'dropdown'
    | 'dynamicdropdown'
    | 'text'
    | 'checkboxgroup'
    | 'composite';

type ChangeValueForFilterType<
    Type extends FilterType,
    Multiple extends boolean | undefined = boolean,
> = Multiple extends true
    ? SelectOption[]
    : Type extends 'dynamicmultiselect' | 'multiselect'
      ? SelectOption[]
      : Type extends
              | 'date'
              | 'offset-date-time'
              | 'dropdown'
              | 'dynamicdropdown'
              | 'text'
              | 'daterange'
              | 'offset-date-time-range'
              | 'checkboxgroup'
        ? SelectOption
        : never;

interface JointFilterProps {
    selectors: string[];
    joinSelector: string;
    comperator?: string;
}

/**
 * What the new design's condition switch offers beyond "is"; the classic drawer reads `canExclude` and
 * `nullOption` instead. "is not" sends `notComparator` alone when there is one (`=notany=`), `=out=` alone
 * on a `neverEmpty` field, and otherwise today's exclusion, which keeps the empty values.
 */
type FilterConditions = { not?: boolean; notComparator?: string; empty?: boolean; neverEmpty?: boolean };

interface BaseCommonFilterProps<Type extends FilterType, Multiple extends boolean | undefined = boolean> {
    type: Type;
    label?: string;
    selector: string;
    tooltip?: string;
    /** Values interpolated into `tooltip`, for a tooltip whose wording depends on runtime data. */
    tooltipValues?: ComponentProps<typeof FormattedMessage>['values'];
    conditions?: FilterConditions;
    /**
     * Message ids of the general controls' (i): the drawer's Exclude toggle and Match select; in the new design `isNot`
     * and `empty` explain the condition switch, `match` its Match row.
     */
    controlTooltips?: { exclude?: string; isNot?: string; empty?: string; match?: string };
    /** Message ids of the line the new design's pane shows under "is empty" / "is not empty", given `label`. */
    emptyCopy?: { empty: string; notEmpty: string };
    /**
     * For a field whose selector is an id: "is empty" / "is not empty" send `<selector><emptyComparator>false` /
     * `…true` instead of the selector's own null test, and a text field offers both.
     */
    emptyComparator?: string;
    group?: string;
    subgroup?: string;
    comperator?: string;
    multiple?: Multiple;
    jointFilter?: JointFilterProps;
    virtual?: boolean;
    getShouldHide?: (props: {
        quickFilters: QuickFilterForm;
        mainFilters: MainFilterForm;
        groupSelector: string;
    }) => boolean;
    preserveValueWhenHidden?: boolean;
    onChange?: (
        val: ChangeValueForFilterType<Type, Multiple> | null,
        groupSelector: string,
        quickFilters: QuickFilterForm,
        handleQuickFilterChange: (value: boolean, groupSelector: string, key: string, immediateFetch?: boolean) => void
    ) => void;
}

type CommonFilterProps<Type extends FilterType> = Type extends
    | 'dynamicdropdown'
    | 'dropdown'
    | 'dynamicmultiselect'
    | 'multiselect'
    ? Omit<BaseCommonFilterProps<Type, false>, 'multiple'>
    : BaseCommonFilterProps<Type>;

type CheckBoxGroupFilter = CommonFilterProps<'checkboxgroup'> & {
    displayDirection?: 'column' | 'row';
    filters: FilterCheckbox[];
    /** Its quick filter's chip names a picked option with the label; see `FilterCheckboxGroup.answerWithLabel`. */
    answerWithLabel?: boolean;
    /** The clause the new design's No answer sends on a single-option yes / no group. */
    no?: { selector: string; comparator: string; argument: string };
    /** In the new design's yes / no reading of a `true` / `false` pair, the `false` option is Yes. */
    falseIsYes?: boolean;
    /** Message ids for the new design's yes / no pane: the help under its title, in place of the tooltip,
     *  and the line under each answer. Without them the pane shows the tooltip and bare answers. */
    yesNoCopy?: { help: string; yes?: string; no?: string };
    /** The new design's yes / no pane titles each answer, and its footer and chip read the answer, by the option's
     *  label ("Has no email") instead of Yes / No. */
    namedAnswers?: boolean;
};

type DateFilter = CommonFilterProps<'date' | 'offset-date-time'> & {};

type DateRangeFilter = CommonFilterProps<'daterange' | 'offset-date-time-range'> & {
    labelTo?: string;
    tooltipTo?: string;
    shouldDisplayQuickPick: boolean;
};

type IntervalFilter = CommonFilterProps<'interval'> & {
    min?: number;
    max?: number;
    minPlaceholder?: string;
    maxPlaceholder?: string;
    minLabel?: string;
    maxLabel?: string;
    minTooltip?: string;
    maxTooltip?: string;
};

/**
 * A single number the user types, compared against one selector — the drawer's counterpart to
 * {@link IntervalFilter}'s two-ended range, for a threshold that only has one end ("at least N days
 * overdue"). The comparison is the filter's own `comperator`, defaulting to `=ge=`.
 */
type NumberFilter = CommonFilterProps<'number'> & {
    min?: number;
    max?: number;
    placeholder?: string;
};

type MultiselectFilter = CommonFilterProps<'multiselect'> & {
    options: SelectOption[];
    canExclude?: boolean;
    nullOption?: NullFilterOption;
    /**
     * "Is not empty" beside `nullOption`'s "is empty": the two head the list above a divider and exclude each other,
     * the values and the exclusion, and the new design's switch offers both.
     */
    notEmptyOption?: NullFilterOption;
};

type DynamicMultiselectFilter = CommonFilterProps<'dynamicmultiselect'> & {
    paginated: PaginatedListProps<DynamicFilterOption> & PaginatedListOptionProps;
    getOptionStartAdornment?: ((option: DynamicFilterOption) => React.ReactNode) | string;
    getOptionEndAdornment?: ((option: DynamicFilterOption) => React.ReactNode) | string;
    canExclude?: boolean;
    nullOption?: NullFilterOption;
    /** "Is not empty" beside `nullOption`'s "is empty", the two excluding each other and the values. */
    notEmptyOption?: NullFilterOption;
    /**
     * Opt-in discriminator for a domain-specific variant of the filter piece. `'contact'` routes
     * through `ContactDynamicMultiselect` (and + Filter's checklist through its `withContactAdornments`),
     * which auto-wires the party-number + type-icon start adornment and the Persona non grata chip at the
     * end. Other subtypes can be added later.
     */
    subType?: 'contact';
};

type DropdownFilter = CommonFilterProps<'dropdown'> & {
    options: SelectOption[];
};

type DynamicDropdownFilter = CommonFilterProps<'dynamicdropdown'> & {
    paginated: PaginatedListProps<DynamicFilterOption> & PaginatedListOptionProps;
    getOptionStartAdornment?: ((option: DynamicFilterOption) => React.ReactNode) | string;
    getOptionEndAdornment?: ((option: DynamicFilterOption) => React.ReactNode) | string;
};

type TextFilter = CommonFilterProps<'text'> & {
    /**
     * Multi-value input where Enter commits a term instead of a comma. For fields whose values
     * routinely contain commas, so comma-splitting would cut a single term in two.
     */
    commitOnEnter?: boolean;
    /**
     * Offers matching every term or none as well as any: a Match control beside the terms; "all" sends one clause
     * per term, "none" the comparator's `=not…=` twin.
     */
    matchModes?: boolean;
};

export type DateFilterForm = DateFilter & {
    value: string | null;
};

export type DateRangeFilterForm = DateRangeFilter & {
    from: string | null;
    to: string | null;
    /** "is empty": `selector=isnull=true`, sent only while no bound is set. */
    isEmpty?: boolean;
};

export type IntervalFilterForm = IntervalFilter & {
    from: number | null;
    to: number | null;
};

export type NumberFilterForm = NumberFilter & {
    /** `null` is "no threshold typed"; `0` is a threshold of zero, so the two must stay distinguishable. */
    value: number | null;
};

export type MultiselectFilterForm = MultiselectFilter & {
    selected: SelectOption[];
    isExcluding?: boolean;
};

export type DynamicMultiselectFilterForm = DynamicMultiselectFilter & {
    selected: SelectOption[];
    isExcluding?: boolean;
};

export type DropdownFilterForm = DropdownFilter & {
    selected: SelectOption | null;
};

export type DynamicDropdownFilterForm = DynamicDropdownFilter & {
    selected: SelectOption | null;
};

export type TextFilterForm = TextFilter & {
    value: string | string[] | null;
    /** "is empty": null or blank, sent only while no value is typed. */
    isEmpty?: boolean;
    /** "is not empty", under an `emptyComparator`, sent only while no value is typed. */
    isNotEmpty?: boolean;
    /** Under `matchModes`, whether a value matches any of the terms, every one or none; any when unset. */
    match?: TextMatch;
};

export type TextMatch = 'any' | 'all' | 'none';

export type CheckBoxGroupFilterForm = Omit<CheckBoxGroupFilter, 'filters'> & {
    displayDirection?: 'column' | 'row';
    filters: FilterCheckboxInForm[];
};

export type CompositePartForm = (
    | MultiselectFilterForm
    | (DateRangeFilterForm & { type: 'daterange' })
    | NumberFilterForm
    | IntervalFilterForm
    | DropdownFilterForm
    | CheckBoxGroupFilterForm
) &
    Pick<CompositePart, 'chipText'>;

export type CompositeFilterForm = Omit<CompositeFilter, 'parts'> & {
    parts: CompositePartForm[];
    isExcluding?: boolean;
};

/** One input of a {@link CompositeFilter}, drawn by its own control. */
export type CompositePart = (
    | MultiselectFilter
    | (DateRangeFilter & { type: 'daterange' })
    | NumberFilter
    | IntervalFilter
    | DropdownFilter
    | CheckBoxGroupFilter
) & {
    /** The chip's words for the part's value, a message id taking `{value}`; the value alone without it. */
    chipText?: string;
};

/**
 * A filter of several parts that sends one clause, `selector<comperator>("…",…)`: each part's arguments in the parts'
 * order, an open end as `""`, then the values of the one list part (options or checkboxes). A dropdown part only
 * qualifies the others: it starts on its first option, and alone sends nothing. With `canExclude`, "is not" sends
 * the comparator's `=not…=` twin. One row, one pane and one chip in the new design; one row of controls under the
 * label in the drawer.
 */
export type CompositeFilter = CommonFilterProps<'composite'> & {
    comperator: string;
    parts: CompositePart[];
    canExclude?: boolean;
};

export type MainFilter =
    | DateFilter
    | DateRangeFilter
    | IntervalFilter
    | NumberFilter
    | MultiselectFilter
    | DynamicMultiselectFilter
    | DropdownFilter
    | DynamicDropdownFilter
    | TextFilter
    | CheckBoxGroupFilter
    | CompositeFilter;

export type MainFilterInForm =
    | DateFilterForm
    | DateRangeFilterForm
    | IntervalFilterForm
    | NumberFilterForm
    | MultiselectFilterForm
    | DynamicMultiselectFilterForm
    | DynamicDropdownFilterForm
    | DropdownFilterForm
    | TextFilterForm
    | CheckBoxGroupFilterForm
    | CompositeFilterForm;

export type MainFilterForm = {
    [key: string]: MainFilterInForm;
};
